import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, AlertCircle, ArrowDownCircle, ArrowUpCircle, ShieldOff, Clock, RefreshCw, X,
} from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadModels, captureEmbedding } from '../../lib/faceApi';
import { GateSaidaChamadosModal, type PendenciasSaida } from './GateSaidaChamadosModal';

interface EventoDia {
  id: number;
  tipo: 'entrada' | 'saida';
  hora: string;
  bypass: boolean;
  bypass_motivo: string | null;
  auto: boolean;
}

interface DiaResp {
  controle_ativo: boolean;
  data: string;
  eventos: EventoDia[];
  proxima_acao: 'entrada' | 'saida';
}

type Props = {
  /**
   * Chamado depois de uma batida aceita pelo servidor (FR ou bypass).
   *
   * Existe pra tela de ENTRADA DO DIA (`/ponto/entrada-do-dia`), que precisa recarregar a
   * pagina inteira quando a pessoa destrava: o `/me` tem que ser relido pra derrubar o
   * `ponto_pendente`, e o socket foi desconectado em definitivo no handshake — ele nao
   * reconecta sozinho de proposito ([socket.tsx], lista de erros terminais). Sem prop, o
   * comportamento e' o de sempre: so recarrega o dia e fica na tela.
   */
  aoRegistrar?: (tipo: 'entrada' | 'saida') => void;
};

export function PontoRegistrar({ aoRegistrar }: Props = {}) {
  const { usuario } = useAuth();
  const navigate = useNavigate();

  const sair = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }, [navigate]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !e.defaultPrevented) sair();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [sair]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [carregandoModelos, setCarregandoModelos] = useState(true);
  const [erroCamera, setErroCamera] = useState('');
  const [carregandoDia, setCarregandoDia] = useState(true);
  const [dia, setDia] = useState<DiaResp>({ controle_ativo: false, data: '', eventos: [], proxima_acao: 'entrada' });
  const [acao, setAcao] = useState<'entrada' | 'saida' | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  const [mensagemTipo, setMensagemTipo] = useState<'info' | 'success' | 'error'>('info');
  const [bypassAberto, setBypassAberto] = useState<'entrada' | 'saida' | null>(null);
  const [bypassMotivo, setBypassMotivo] = useState('');
  // Gate de saida (RDS 1.17): chamados em aberto barram a saida ate resolver/congelar/transferir.
  const [gatePend, setGatePend] = useState<PendenciasSaida | null>(null);
  // 'fr' ou 'bypass' — como retomar a saida depois de resolver os chamados no gate.
  const [gateModo, setGateModo] = useState<'fr' | 'bypass'>('fr');

  const carregarDia = useCallback(async () => {
    setCarregandoDia(true);
    try {
      const data = await api.get<DiaResp>('/api/ponto/dia');
      setDia(data);
    } catch (err: any) {
      setMensagemTipo('error');
      setMensagem('Erro ao carregar status do dia: ' + err.message);
    } finally {
      setCarregandoDia(false);
    }
  }, []);

  useEffect(() => { void carregarDia(); }, [carregarDia]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        await loadModels();
        if (!cancelado) setCarregandoModelos(false);
      } catch {
        if (!cancelado) {
          setErroCamera('Falha ao carregar modelos de FR');
          setCarregandoModelos(false);
        }
      }
    })();
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    let cancelado = false;
    async function iniciar() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setErroCamera('Navegador nao suporta camera');
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 480, height: 360, facingMode: 'user' },
          audio: false,
        });
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        const track = stream.getVideoTracks()[0];
        if (!track || track.readyState !== 'live') {
          stream.getTracks().forEach((t) => t.stop());
          setErroCamera('Camera em uso por outra aba/app. Use o bypass se precisar.');
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch {}
        }
      } catch (err: any) {
        const name = err?.name || '';
        if (name === 'NotAllowedError') setErroCamera('Permissao da camera negada. Cadeado da URL > Camera > Permitir > F5.');
        else if (name === 'NotReadableError') setErroCamera('Camera em uso por outra aba/app. Use o bypass se precisar.');
        else setErroCamera('Erro de camera (' + name + ')');
      }
    }
    void iniciar();
    return () => {
      cancelado = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  async function registrarFR(tipo: 'entrada' | 'saida') {
    if (!videoRef.current || enviando) return;
    setAcao(tipo);
    setEnviando(true);
    setMensagem('');
    try {
      const embedding = await captureEmbedding(videoRef.current);
      if (!embedding) {
        setMensagemTipo('error');
        setMensagem('Rosto nao detectado. Centralize-se na camera e tente de novo.');
        return;
      }
      await api.post(`/api/ponto/${tipo}`, { embedding });
      setMensagemTipo('success');
      setMensagem(tipo === 'entrada' ? 'Entrada registrada com sucesso!' : 'Saida registrada com sucesso!');
      await carregarDia();
      aoRegistrar?.(tipo);
    } catch (err: any) {
      const status = err instanceof ApiError ? err.status : 0;
      // err.corpo = corpo JSON completo do erro; o 409 do gate manda { erro, pendencias } no
      // top-level (nao em 'detalhes'). Ler de err.detalhes deixava o gate NUNCA abrir no bypass
      // e na defesa-em-profundidade do FR (so o pre-check de iniciarSaida abria). Idem usuario_nome (403).
      const det = err instanceof ApiError ? (err.corpo as any) : null;
      // Gate de saida (RDS 1.17): tem chamado em aberto — abre a janela em vez de erro.
      if (status === 409 && det?.pendencias) {
        setGateModo('fr');
        setGatePend(det.pendencias as PendenciasSaida);
        return;
      }
      setMensagemTipo('error');
      if (status === 403) setMensagem(`Rosto reconhecido como ${det?.usuario_nome || 'outro usuario'}. Faca login com a conta correta.`);
      else if (status === 404) setMensagem('Rosto nao reconhecido. Aproxime-se mais ou peca pra recadastrar.');
      else setMensagem('Erro: ' + (err.message || 'desconhecido'));
    } finally {
      setEnviando(false);
      setAcao(null);
    }
  }

  async function registrarBypass(tipo: 'entrada' | 'saida') {
    if (bypassMotivo.trim().length < 3) {
      alert('Informe um motivo (minimo 3 caracteres).');
      return;
    }
    setEnviando(true);
    setAcao(tipo);
    try {
      await api.post(`/api/ponto/${tipo}/bypass`, { motivo: bypassMotivo.trim() });
      setMensagemTipo('success');
      setMensagem(`${tipo === 'entrada' ? 'Entrada' : 'Saida'} (bypass) registrada.`);
      setBypassAberto(null);
      setBypassMotivo('');
      await carregarDia();
      aoRegistrar?.(tipo);
    } catch (err: any) {
      const status = err instanceof ApiError ? err.status : 0;
      // err.corpo = corpo JSON completo do erro; o 409 do gate manda { erro, pendencias } no
      // top-level (nao em 'detalhes'). Ler de err.detalhes deixava o gate NUNCA abrir no bypass
      // e na defesa-em-profundidade do FR (so o pre-check de iniciarSaida abria). Idem usuario_nome (403).
      const det = err instanceof ApiError ? (err.corpo as any) : null;
      if (status === 409 && det?.pendencias) {
        setGateModo('bypass');
        setGatePend(det.pendencias as PendenciasSaida);
        return;
      }
      setMensagemTipo('error');
      setMensagem('Erro: ' + err.message);
    } finally {
      setEnviando(false);
      setAcao(null);
    }
  }

  // Saida com gate (RDS 1.17): pre-checa chamados em aberto ANTES da camera. Se houver,
  // abre a janela do gate; senao segue pro FR normal.
  async function iniciarSaida() {
    if (enviando) return;
    try {
      const pend = await api.get<PendenciasSaida>('/api/ponto/saida/pendencias');
      if (pend.bloqueia) {
        setGateModo('fr');
        setGatePend(pend);
        return;
      }
    } catch {
      // Falha no pre-check nao trava a saida — o POST /saida re-checa server-side (409).
    }
    await registrarFR('saida');
  }

  // Re-checa as pendencias depois de uma acao no gate. Zerou -> fecha o gate e retoma a saida.
  async function recarregarGate() {
    const pend = await api.get<PendenciasSaida>('/api/ponto/saida/pendencias');
    if (!pend.bloqueia) {
      setGatePend(null);
      if (gateModo === 'bypass') await registrarBypass('saida');
      else await registrarFR('saida');
      return;
    }
    setGatePend(pend);
  }

  async function congelarESair() {
    await api.post('/api/ponto/saida/congelar-e-sair', {});
    setGatePend(null);
    if (gateModo === 'bypass') await registrarBypass('saida');
    else await registrarFR('saida');
  }

  async function encerrarTodosESair() {
    await api.post('/api/ponto/saida/encerrar-pendencias', {});
    setGatePend(null);
    if (gateModo === 'bypass') await registrarBypass('saida');
    else await registrarFR('saida');
  }

  function abrirConversaDoGate(conversaId: number) {
    localStorage.setItem('fiodecobre_conversa_ativa', String(conversaId));
    navigate('/atendimento');
  }

  if (!dia.controle_ativo && !carregandoDia) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg border border-gray-200 p-6 mt-8 text-center relative" style={{ background: 'var(--card-bg)' }}>
        <button
          onClick={sair}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
          title="Fechar (ESC)"
        >
          <X size={18} />
        </button>
        <Clock size={48} className="text-gray-400 mx-auto mb-3" />
        <h2 className="text-lg font-semibold mb-2">Controle de Acesso</h2>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          O controle de acesso por reconhecimento facial nao esta ativo pra voce.
          Peca ao admin/gerente para ativar nas configuracoes do seu usuario.
        </p>
      </div>
    );
  }

  const proxima = dia.proxima_acao || 'entrada';
  const ultimo = dia.eventos[dia.eventos.length - 1];

  return (
    <div className="max-w-4xl mx-auto rounded-xl shadow-md border border-gray-300 p-5 my-2" style={{ background: 'var(--card-bg)' }}>
      <div className="pagina-header">
        <div>
          <h2 className="pagina-titulo">Controle de Acesso</h2>
          <div className="text-sm" style={{ color: 'var(--text-muted)' }}>{usuario?.nome}</div>
        </div>
        <div className="pagina-acoes">
          <button
            onClick={() => void carregarDia()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
          <button
            onClick={sair}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
            title="Fechar (ESC)"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border border-gray-200 p-4" style={{ background: 'var(--page-bg)' }}>
          <h2 className="text-sm font-semibold mb-3">Camera</h2>
          <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
            {(carregandoModelos || erroCamera) && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/75 text-white text-sm text-center px-4">
                {erroCamera ? (
                  <div className="flex flex-col items-center gap-2">
                    <AlertCircle size={20} className="text-orange-400" />
                    <div>{erroCamera}</div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Loader2 size={18} className="animate-spin" /> Carregando modelos...
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => void registrarFR('entrada')}
              disabled={proxima !== 'entrada' || enviando || !!erroCamera || carregandoModelos}
              className={`flex flex-col items-center justify-center gap-1 py-4 rounded-lg font-semibold text-white transition disabled:opacity-40 disabled:cursor-not-allowed ${
                proxima === 'entrada' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'
              }`}
            >
              {enviando && acao === 'entrada' ? <Loader2 size={20} className="animate-spin" /> : <ArrowDownCircle size={22} />}
              <span>Registrar Entrada</span>
            </button>
            <button
              onClick={() => void iniciarSaida()}
              disabled={proxima !== 'saida' || enviando || !!erroCamera || carregandoModelos}
              className={`flex flex-col items-center justify-center gap-1 py-4 rounded-lg font-semibold text-white transition disabled:opacity-40 disabled:cursor-not-allowed ${
                proxima === 'saida' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-400'
              }`}
            >
              {enviando && acao === 'saida' ? <Loader2 size={20} className="animate-spin" /> : <ArrowUpCircle size={22} />}
              <span>Registrar Saida</span>
            </button>
          </div>

          {mensagem && (
            <div className={`mt-3 text-sm px-3 py-2 rounded-lg ${
              mensagemTipo === 'success' ? 'bg-green-50 text-green-700' :
              mensagemTipo === 'error' ? 'bg-red-50 text-red-700' :
              'bg-blue-50 text-blue-700'
            }`}>
              {mensagem}
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-200">
            {bypassAberto ? (
              <div className="space-y-2">
                <label className="block text-xs font-medium">
                  Motivo do bypass — {bypassAberto}:
                </label>
                <input
                  type="text"
                  value={bypassMotivo}
                  onChange={(e) => setBypassMotivo(e.target.value)}
                  placeholder="Ex: camera quebrada"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setBypassAberto(null); setBypassMotivo(''); }}
                    className="flex-1 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
                    disabled={enviando}
                  >Cancelar</button>
                  <button
                    onClick={() => void registrarBypass(bypassAberto)}
                    disabled={enviando}
                    className="flex-1 px-3 py-2 text-sm bg-orange-600 text-white hover:bg-orange-700 rounded-lg disabled:opacity-50"
                  >{enviando ? 'Enviando...' : 'Confirmar bypass'}</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setBypassAberto(proxima)}
                className="w-full text-xs hover:underline inline-flex items-center justify-center gap-1.5"
                style={{ color: 'var(--text-muted)' }}
              >
                <ShieldOff size={12} />
                Nao consigo bater agora — registrar {proxima} com bypass
              </button>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 p-4" style={{ background: 'var(--page-bg)' }}>
          <h2 className="text-sm font-semibold mb-3">Batidas de hoje</h2>
          {carregandoDia ? (
            <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
              <Loader2 size={20} className="animate-spin mx-auto" />
            </div>
          ) : dia.eventos.length === 0 ? (
            <div className="text-center text-sm py-8" style={{ color: 'var(--text-muted)' }}>
              Nenhuma batida hoje. Comece registrando sua entrada.
            </div>
          ) : (
            <ol className="space-y-2">
              {dia.eventos.map((e, i) => {
                const isEntrada = e.tipo === 'entrada';
                const Icon = isEntrada ? ArrowDownCircle : ArrowUpCircle;
                return (
                  <li key={e.id} className={`flex items-start gap-3 p-2 rounded-lg ${
                    isEntrada ? 'bg-green-50' : 'bg-orange-50'
                  }`}>
                    <Icon size={20} className={isEntrada ? 'text-green-600' : 'text-orange-600'} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm capitalize">
                        {e.tipo} — {new Date(e.hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </div>
                      {e.bypass && (
                        <div className="text-xs text-orange-700">
                          Bypass: {e.bypass_motivo}
                        </div>
                      )}
                      {e.auto && (
                        <div className="text-xs text-purple-700">
                          Saida automatica (esqueceu de bater)
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">#{i + 1}</div>
                  </li>
                );
              })}
            </ol>
          )}

          {ultimo && (
            <div className="mt-4 pt-3 border-t border-gray-200 text-xs" style={{ color: 'var(--text-muted)' }}>
              Ultima batida: <b className="capitalize">{ultimo.tipo}</b> as {new Date(ultimo.hora).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              <br />Proxima esperada: <b className="capitalize">{proxima}</b>
            </div>
          )}
        </div>
      </div>

      {gatePend && (
        <GateSaidaChamadosModal
          pendencias={gatePend}
          onRecarregar={recarregarGate}
          onCongelarESair={congelarESair}
          onEncerrarTodosESair={encerrarTodosESair}
          onAbrir={abrirConversaDoGate}
          onFechar={() => setGatePend(null)}
        />
      )}
    </div>
  );
}
